import { FC, useEffect, useState } from "react";
import { Menu } from "antd";
import { NavLink, useLocation } from "react-router-dom";
import { connect } from "react-redux";
import {
  TeamOutlined,
  FileTextOutlined,
  ContainerOutlined,
  GoldOutlined,
  TruckOutlined,
  ToolOutlined,
  PercentageOutlined,
  ProductOutlined,
  ShoppingCartOutlined,
  FileSearchOutlined,
  UserOutlined,
  UsergroupAddOutlined,
  BellOutlined,
  DollarOutlined,
  AuditOutlined,
  SettingOutlined,
  SafetyCertificateOutlined,
  BarChartOutlined,
  WalletOutlined,
  CalculatorOutlined,
  HomeOutlined,
  HistoryOutlined,
  SecurityScanOutlined,
  BookOutlined,
  GiftOutlined,
  CarOutlined,
  MobileOutlined,
} from "@ant-design/icons";
import { BankOutlined } from "@ant-design/icons";
import { Global } from "../../constants";
import { DataService } from "../../services/http-interceptor.service";
import { usePermissions } from "../../hooks/usePermissions.hook";

type Props = {
  topMenu?: Boolean;
  inlineCollapsed?: Boolean | any;
  user?: any;
  theme?: "light" | "dark";
};

const NavMenu: FC<Props> = ({
  topMenu,
  inlineCollapsed,
  user,
  theme = "light",
}) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [unreadWarrantyTicketsCount, setUnreadWarrantyTicketsCount] =
    useState(0);
  const location = useLocation();
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const { canManage, hasAccessTo } = usePermissions();

  const hasBonosAccess = () => {
    const permissions = (!user?.permissionMode || user?.permissionMode === 'heredar')
      ? user?.roleId?.permissionId?.permissions
      : user?.permissionId?.permissions;
    return permissions?.some((p: any) => p.submodule === 'bonos' && p.accessLevel !== 'none') ?? false;
  };
  const [pendingWarehouse, setPendingWarehouse] = useState({
    transfers: 0,
    stockModifications: 0,
    total: 0,
  });
  const [pendingExpensesCount, setPendingExpensesCount] = useState(0);
  const [pendingToolAdjustments, setPendingToolAdjustments] = useState(0);
  const [pendingDiscounts, setPendingDiscounts] = useState(0);
  // Aprobaciones/confirmaciones de pago pendientes para el jefe (garantías).
  const [pendingWarrantyApprovals, setPendingWarrantyApprovals] = useState(0);

  const fetchUnreadCount = async () => {
    try {
      const response = await DataService.get("commentProyect/unread-counts");
      setUnreadCount(response.data.total || 0);
    } catch (error) {
      console.error("Error fetching unread counts:", error);
    }
  };

  const fetchUnreadWarrantyTickets = async () => {
    try {
      const response = await DataService.get(
        "warranty-tickets/internal/counts",
      );
      if (response && response.status === 200) {
        setUnreadWarrantyTicketsCount(response.data.PENDIENTE || 0);
      }
    } catch (error) {
      console.error("Error fetching unread warranty tickets:", error);
    }
  };

  const fetchPendingWarehouse = async () => {
    try {
      const response = await DataService.get("warehouses/pending-counts");
      setPendingWarehouse(response.data);
    } catch (error) {
      console.error("Error fetching pending warehouse counts:", error);
    }
  };

  const fetchPendingToolAdjustments = async () => {
    try {
      const response = await DataService.get("tool-adjustments?status=PENDING&limit=1");
      const data = response.data;
      setPendingToolAdjustments(data?.totalResults || data?.totalDocs || 0);
    } catch (error) {
      console.error("Error fetching pending tool adjustments:", error);
    }
  };

  const fetchPendingWarrantyApprovals = async () => {
    try {
      const [aprob, rev] = await Promise.all([
        DataService.get('warranties?status=APROBACION_PENDIENTE&limit=1'),
        DataService.get('warranties?status=REVISION_PRECIO&limit=1'),
      ]);
      const total =
        (aprob?.data?.totalResults || 0) + (rev?.data?.totalResults || 0);
      setPendingWarrantyApprovals(total);
    } catch (error) {
      console.error('Error fetching pending warranty approvals:', error);
    }
  };

  const fetchPendingDiscounts = async () => {
    try {
      const response = await DataService.get("employee-discounts?status=PENDIENTE&limit=1");
      const data = response.data;
      setPendingDiscounts(data?.totalResults || data?.totalDocs || 0);
    } catch (error) {
      console.error("Error fetching pending discounts:", error);
    }
  };

  const fetchPendingExpenses = async () => {
    try {
      const response = await DataService.get(
        "employeesexpenses?status=PENDIENTE,DELETE&isActive=true",
      );
      const data = response.data;
      let total = 0;
      if (Array.isArray(data)) {
        total = data.length;
      } else if (data?.results && Array.isArray(data.results)) {
        total = data.totalResults ?? data.results.length;
      } else if (typeof data?.totalResults === "number") {
        total = data.totalResults;
      }
      setPendingExpensesCount(total);
    } catch (error) {
      console.error("Error fetching pending expenses:", error);
    }
  };

  useEffect(() => {
    if (user) {
      fetchUnreadCount();
      if (canManage("traspasos", "almacen") || canManage("ajustes", "almacen")) {
        fetchPendingWarehouse();
      }
      fetchUnreadWarrantyTickets();
      if (hasAccessTo("ajustes", "herramientas")) {
        fetchPendingToolAdjustments();
      }
      fetchPendingDiscounts();
      if (canManage("gestion", "garantias")) {
        fetchPendingWarrantyApprovals();
      }
      if (hasAccessTo("gastos", "rrhh") || hasAccessTo("wallet")) {
        fetchPendingExpenses();
      }

      window.addEventListener("updateUnreadCounts", fetchUnreadCount);
      window.addEventListener("updateWarehouseCounts", fetchPendingWarehouse);
      window.addEventListener("updatePendingExpenses", fetchPendingExpenses);
      window.addEventListener(
        "updateWarrantyTicketsCounts",
        fetchUnreadWarrantyTickets,
      );

      return () => {
        window.removeEventListener("updateUnreadCounts", fetchUnreadCount);
        window.removeEventListener(
          "updateWarehouseCounts",
          fetchPendingWarehouse,
        );
        window.removeEventListener(
          "updatePendingExpenses",
          fetchPendingExpenses,
        );
        window.removeEventListener(
          "updateWarrantyTicketsCounts",
          fetchUnreadWarrantyTickets,
        );
      };
    }
  }, [user]);

  useEffect(() => {
    // Definir mapeo de rutas a keys del menú
    const path = location.pathname;

    fetchUnreadWarrantyTickets();

    // Mapeo simple de rutas a keys
    const menuMapping: Record<string, { key: string; parent?: string }> = {
      [Global.APP_URLS.DASHBOARD]: { key: "home" },
      [Global.APP_URLS.PROYECTS]: { key: "box" },
      [Global.APP_URLS.NOTIFICATIONS]: { key: "notifications" },
      [Global.APP_URLS.NOVEDADES]: { key: "novedades" },
      [Global.APP_URLS.EMPLOYEES]: { key: "employees", parent: "hr" },
      [Global.APP_URLS.EMPLOYEES_EXPENSES]: { key: "expenses", parent: "hr" },
      [Global.APP_URLS.PAYROLL]: { key: "payroll", parent: "hr" },
      "/admin/loans": { key: "loans", parent: "hr" },
      [Global.APP_URLS.EMPLOYEE_DISCOUNTS]: { key: "descuentos", parent: "hr" },
      "/admin/wallet": { key: "wallet", parent: "hr" },
      "/admin/bonos": { key: "bonos", parent: "hr" },
      [Global.APP_URLS.ADMINS]: { key: "admins", parent: "configuracion" },
      [Global.APP_URLS.ROLES]: { key: "roles", parent: "configuracion" },
      [Global.APP_URLS.TEMPLATES]: { key: "document" },
      [Global.APP_URLS.COMPANIES]: { key: "branchs" },
      [Global.APP_URLS.VEHICLES]: { key: "vehicles", parent: "vehiculos" },
      [Global.APP_URLS.VEHICLE_LOANS]: { key: "vehicle-loans", parent: "vehiculos" },
      [Global.APP_URLS.ACTIVES]: { key: "actives", parent: "activos" },
      [Global.APP_URLS.ACTIVE_LOANS]: { key: "active-loans", parent: "activos" },
      [Global.APP_URLS.INVOICES]: { key: "invoices", parent: "comprobantes" },
      [Global.APP_URLS.CARTA_PORTE]: { key: "cartaPorte", parent: "comprobantes" },
      [Global.APP_URLS.CUSTOMER_QUOTATIONS]: { key: "customer-quotations" },
      [Global.APP_URLS.PRODUCTS]: { key: "products" },
      [Global.APP_URLS.SHOPPING]: { key: "compras" },
      [Global.APP_URLS.PENDING_PRODUCTS]: {
        key: "pendientes",
        parent: "almacen",
      },
      [Global.APP_URLS.WAREHOUSES]: { key: "almacenes", parent: "almacen" },
      [Global.APP_URLS.SEMINUEVOS]: { key: "almacenes", parent: "almacen" },
      [Global.APP_URLS.MOVEMENTS]: { key: "movimientos", parent: "almacen" },
      [Global.APP_URLS.TRANSFERS]: { key: "traspasos", parent: "almacen" },
      [Global.APP_URLS.STOCK_MODIFICATIONS]: {
        key: "ajustes",
        parent: "almacen",
      },
      [Global.APP_URLS.REPORTS]: { key: "reports" },
      [Global.APP_URLS.WARRANTIES]: { key: "warranties" },
      [Global.APP_URLS.WARRANTY_TICKETS]: { key: "tickets_garantia" },
      [Global.APP_URLS.BITACORAS]: { key: "bitacoras" },
      [Global.APP_URLS.TOOLS_CATALOG]: {
        key: "tools-catalog",
        parent: "herramientas",
      },
      [Global.APP_URLS.TOOLS_STOCK]: {
        key: "tools-stock",
        parent: "herramientas",
      },
      [Global.APP_URLS.TOOLS_ADJUSTMENTS]: {
        key: "tools-adjustments",
        parent: "herramientas",
      },
      [Global.APP_URLS.TOOLS_MOVEMENTS]: {
        key: "tools-movements",
        parent: "herramientas",
      },
    };

    // 1. Intentar match exacto primero
    let matched = menuMapping[path];

    // 2. Si no hay match exacto, intentar por prefijo (para rutas con params)
    // EXCEPTO para /admin, que solo debe matchear exacto
    if (!matched) {
      const entry = Object.entries(menuMapping).find(
        ([route]) => route !== "/admin" && path.startsWith(route),
      );
      if (entry) matched = entry[1];
    }

    if (matched) {
      setSelectedKeys([matched.key]);
      if (matched.parent && !openKeys.includes(matched.parent)) {
        setOpenKeys((prev) => {
          if (prev.includes(matched!.parent!)) return prev;
          return [...prev, matched!.parent!];
        });
      }
    }
  }, [location.pathname]);

  // Pending counts must only aggregate submodules the user can actually see
  const visiblePendingWarehouseTotal =
    (canManage("traspasos", "almacen") ? pendingWarehouse.transfers : 0) +
    (canManage("ajustes", "almacen") ? pendingWarehouse.stockModifications : 0);
  const canSeeToolAdjustments = hasAccessTo("ajustes", "herramientas");

  const onOpenChange = (keys: string[]) => {
    setOpenKeys(keys);
  };

  return (
    <Menu
      mode={!topMenu || window.innerWidth <= 991 ? "inline" : "horizontal"}
      theme={theme}
      inlineCollapsed={inlineCollapsed}
      selectedKeys={selectedKeys}
      openKeys={openKeys}
      onOpenChange={onOpenChange}
      style={{ borderRight: 0 }}
    >
      <Menu.Item icon={!topMenu && <HomeOutlined />} key="home">
        <NavLink to={Global.APP_URLS.DASHBOARD}>Inicio</NavLink>
      </Menu.Item>

      {hasAccessTo("menuProyectos") && (
        <Menu.Item icon={!topMenu && <FileTextOutlined />} key="box">
          <NavLink to={Global.APP_URLS.PROYECTS}>Proyectos</NavLink>
        </Menu.Item>
      )}

      {hasAccessTo("centro") && (
        <Menu.Item icon={!topMenu && <BellOutlined />} key="notifications">
          <NavLink to={Global.APP_URLS.NOTIFICATIONS}>Notificaciones</NavLink>
        </Menu.Item>
      )}

      {/* Moved Novedades below reports */}

      {(hasAccessTo("rrhh") ||
        hasAccessTo("empleados") ||
        hasAccessTo("gastos", "rrhh") ||
        hasAccessTo("nominas") ||
        hasAccessTo("prestamos") ||
        hasAccessTo("wallet") ||
        hasBonosAccess()) && (
          <Menu.SubMenu
            icon={!topMenu && <UsergroupAddOutlined />}
            key="hr"
            title={
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                  paddingRight: "20px",
                }}
              >
                <span>RH</span>
                {(pendingExpensesCount > 0 || pendingDiscounts > 0) && (
                  <div
                    style={{
                      backgroundColor: "#ff4d4f",
                      color: "#fff",
                      borderRadius: "10px",
                      minWidth: "20px",
                      height: "20px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "11px",
                      padding: "0 6px",
                      fontWeight: "bold",
                    }}
                  >
                    {pendingExpensesCount + pendingDiscounts > 99
                      ? "99+"
                      : pendingExpensesCount + pendingDiscounts}
                  </div>
                )}
              </div>
            }
          >
            {hasAccessTo("empleados") && (
              <Menu.Item icon={!topMenu && <UserOutlined />} key="employees">
                <NavLink to={Global.APP_URLS.EMPLOYEES}>Empleados</NavLink>
              </Menu.Item>
            )}
            {hasAccessTo("gastos", "rrhh") && (
              <Menu.Item
                icon={!topMenu && <AuditOutlined />}
                key="expenses"
                title="Gastos"
              >
                <NavLink to={Global.APP_URLS.EMPLOYEES_EXPENSES}>
                  <span
                    style={{
                      position: "relative",
                      paddingRight: pendingExpensesCount > 0 ? "28px" : "0",
                    }}
                  >
                    Gastos
                    {pendingExpensesCount > 0 && (
                      <span
                        style={{
                          position: "absolute",
                          top: "-6px",
                          right: 0,
                          backgroundColor: "#ff4d4f",
                          color: "#fff",
                          borderRadius: "10px",
                          minWidth: "20px",
                          height: "18px",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "10px",
                          padding: "0 5px",
                          fontWeight: "bold",
                          lineHeight: 1,
                        }}
                      >
                        {pendingExpensesCount > 99 ? "99+" : pendingExpensesCount}
                      </span>
                    )}
                  </span>
                </NavLink>
              </Menu.Item>
            )}
            {hasAccessTo("nominas") && (
              <Menu.Item icon={!topMenu && <DollarOutlined />} key="payroll">
                <NavLink to={Global.APP_URLS.PAYROLL}>Nóminas</NavLink>
              </Menu.Item>
            )}
            {hasAccessTo("prestamos") && (
              <Menu.Item icon={!topMenu && <CalculatorOutlined />} key="loans">
                <NavLink to="/admin/loans">Préstamos</NavLink>
              </Menu.Item>
            )}
            {hasAccessTo("descuentos") && (
              <Menu.Item icon={!topMenu && <PercentageOutlined />} key="descuentos">
                <NavLink to={Global.APP_URLS.EMPLOYEE_DISCOUNTS}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                      paddingRight: "20px",
                      overflow: "hidden",
                    }}
                  >
                    <span
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Descuentos
                    </span>
                    {pendingDiscounts > 0 && (
                      <div
                        style={{
                          backgroundColor: "#ff4d4f",
                          color: "#fff",
                          borderRadius: "10px",
                          minWidth: "20px",
                          height: "20px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "11px",
                          padding: "0 6px",
                          lineHeight: "1",
                          fontWeight: "bold",
                          marginLeft: "8px",
                          flexShrink: 0,
                        }}
                      >
                        {pendingDiscounts > 99 ? "99+" : pendingDiscounts}
                      </div>
                    )}
                  </div>
                </NavLink>
              </Menu.Item>
            )}
            {hasAccessTo("wallet") && (
              <Menu.Item icon={!topMenu && <WalletOutlined />} key="wallet">
                <NavLink to="/admin/wallet">Wallet</NavLink>
              </Menu.Item>
            )}
            {hasBonosAccess() && (
              <Menu.Item icon={!topMenu && <GiftOutlined />} key="bonos">
                <NavLink to="/admin/bonos">Bonos</NavLink>
              </Menu.Item>
            )}
          </Menu.SubMenu>
        )}

      {(hasAccessTo("configuracion") ||
        hasAccessTo("usuarios") ||
        hasAccessTo("roles")) && (
          <Menu.SubMenu
            icon={!topMenu && <SettingOutlined />}
            key="configuracion"
            title="Configuración"
          >
            {hasAccessTo("usuarios") && (
              <Menu.Item icon={!topMenu && <TeamOutlined />} key="admins">
                <NavLink to={Global.APP_URLS.ADMINS}>Usuarios</NavLink>
              </Menu.Item>
            )}
            {hasAccessTo("roles") && (
              <Menu.Item
                icon={!topMenu && <SafetyCertificateOutlined />}
                key="roles"
              >
                <NavLink to={Global.APP_URLS.ROLES}>Roles</NavLink>
              </Menu.Item>
            )}
          </Menu.SubMenu>
        )}

      {(hasAccessTo("comunicacion-interna", "administracion") ||
        hasAccessTo("politicas", "administracion") ||
        hasAccessTo("documentacion", "administracion")) && (
          <Menu.Item icon={!topMenu && <BankOutlined />} key="administracion">
            <NavLink to={Global.APP_URLS.ADMINISTRATION}>Administración</NavLink>
          </Menu.Item>
        )}

      {hasAccessTo("plantillas") && (
        <Menu.Item icon={!topMenu && <ContainerOutlined />} key="document">
          <NavLink to={Global.APP_URLS.TEMPLATES}>Plantillas</NavLink>
        </Menu.Item>
      )}

      {hasAccessTo("empresas") && (
        <Menu.Item icon={!topMenu && <GoldOutlined />} key="branchs">
          <NavLink to={Global.APP_URLS.COMPANIES}>Empresas</NavLink>
        </Menu.Item>
      )}

      {(hasAccessTo("facturas") || hasAccessTo("cartaPorte")) && (
        <Menu.SubMenu icon={!topMenu && <FileTextOutlined />} key="comprobantes" title="Comprobantes">
          {hasAccessTo("facturas") && (
            <Menu.Item key="invoices">
              <NavLink to={Global.APP_URLS.INVOICES}>Facturas</NavLink>
            </Menu.Item>
          )}
          {hasAccessTo("cartaPorte") && (
            <Menu.Item key="cartaPorte">
              <NavLink to={Global.APP_URLS.CARTA_PORTE}>Carta Porte</NavLink>
            </Menu.Item>
          )}
        </Menu.SubMenu>
      )}

      {hasAccessTo("customer_quotations", "facturas") && (
        <Menu.Item icon={!topMenu && <FileTextOutlined />} key="customer-quotations">
          <NavLink to={Global.APP_URLS.CUSTOMER_QUOTATIONS}>
            Cotizaciones a Clientes
          </NavLink>
        </Menu.Item>
      )}

      {hasAccessTo("productos") && (
        <Menu.Item icon={!topMenu && <ProductOutlined />} key="products">
          <NavLink to={Global.APP_URLS.PRODUCTS}>Productos</NavLink>
        </Menu.Item>
      )}

      {hasAccessTo("compras") && (
        <Menu.Item icon={!topMenu && <ShoppingCartOutlined />} key="compras">
          <NavLink to={Global.APP_URLS.SHOPPING}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <span>Compras</span>
              {unreadCount > 0 && (
                <div
                  id="fixed-sidebar-badge"
                  style={{
                    backgroundColor: "#ff4d4f",
                    color: "#fff",
                    borderRadius: "10px",
                    minWidth: "20px",
                    height: "20px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "12px",
                    padding: "0 6px",
                    lineHeight: "1",
                    fontWeight: "bold",
                  }}
                >
                  {unreadCount > 99 ? "99+" : unreadCount}
                </div>
              )}
            </div>
          </NavLink>
        </Menu.Item>
      )}

      {hasAccessTo("garantias", "almacen") && (
        <Menu.Item icon={!topMenu && <SecurityScanOutlined />} key="warranties">
          <NavLink to={Global.APP_URLS.WARRANTIES}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <span>Garantías</span>
              {pendingWarrantyApprovals > 0 && (
                <div
                  style={{
                    backgroundColor: "#fa541c",
                    color: "#fff",
                    borderRadius: "10px",
                    minWidth: "20px",
                    height: "20px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "12px",
                    padding: "0 6px",
                    lineHeight: "1",
                    fontWeight: "bold",
                  }}
                >
                  {pendingWarrantyApprovals > 99 ? "99+" : pendingWarrantyApprovals}
                </div>
              )}
            </div>
          </NavLink>
        </Menu.Item>
      )}

      {(hasAccessTo("gestion", "tickets_garantia") ||
        hasAccessTo("mis_tickets", "tickets_garantia") ||
        hasAccessTo("codigos", "tickets_garantia")) && (
          <Menu.Item
            icon={!topMenu && <SafetyCertificateOutlined />}
            key="tickets_garantia"
          >
            <NavLink to={Global.APP_URLS.WARRANTY_TICKETS}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                }}
              >
                <span>Soporte</span>
                {unreadWarrantyTicketsCount > 0 && (
                  <div
                    style={{
                      backgroundColor: "#ff4d4f",
                      color: "#fff",
                      borderRadius: "10px",
                      minWidth: "20px",
                      height: "20px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "12px",
                      padding: "0 6px",
                      lineHeight: "1",
                      fontWeight: "bold",
                    }}
                  >
                    {unreadWarrantyTicketsCount > 99
                      ? "99+"
                      : unreadWarrantyTicketsCount}
                  </div>
                )}
              </div>
            </NavLink>
          </Menu.Item>
        )}

      {hasAccessTo("bitacoras") && (
        <Menu.Item icon={!topMenu && <BookOutlined />} key="bitacoras">
          <NavLink to={Global.APP_URLS.BITACORAS}>Bitácoras</NavLink>
        </Menu.Item>
      )}

      {hasAccessTo("almacen") && (
        <Menu.SubMenu
          icon={!topMenu && <TruckOutlined />}
          title={
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                paddingRight: "20px",
                overflow: "hidden",
              }}
            >
              <span
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                Almacén
              </span>
              {visiblePendingWarehouseTotal > 0 && (
                <div
                  style={{
                    backgroundColor: "#ff4d4f",
                    color: "#fff",
                    borderRadius: "10px",
                    minWidth: "20px",
                    height: "20px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "11px",
                    padding: "0 6px",
                    lineHeight: "1",
                    fontWeight: "bold",
                    marginLeft: "8px",
                    flexShrink: 0,
                  }}
                >
                  {visiblePendingWarehouseTotal > 99
                    ? "99+"
                    : visiblePendingWarehouseTotal}
                </div>
              )}
            </div>
          }
          key="almacen"
        >
          {hasAccessTo("pendientes", "almacen") && (
            <Menu.Item key="pendientes">
              <NavLink to={Global.APP_URLS.PENDING_PRODUCTS}>
                Productos pendientes
              </NavLink>
            </Menu.Item>
          )}
          {hasAccessTo("gestion", "almacen") && (
            <Menu.Item key="almacenes">
              <NavLink to={Global.APP_URLS.WAREHOUSES}>Almacenes</NavLink>
            </Menu.Item>
          )}
          {/* Seminuevos vive DENTRO de Almacenes como otro almacén (tarjeta Seminuevos). */}
          {hasAccessTo("movimientos", "almacen") && (
            <Menu.Item key="movimientos">
              <NavLink to={Global.APP_URLS.MOVEMENTS}>Movimientos</NavLink>
            </Menu.Item>
          )}
          {hasAccessTo("traspasos", "almacen") && (
            <Menu.Item key="traspasos">
              <NavLink to={Global.APP_URLS.TRANSFERS}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                    overflow: "hidden",
                  }}
                >
                  <span
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Traspasos
                  </span>
                  {pendingWarehouse.transfers > 0 && (
                    <div
                      style={{
                        backgroundColor: "#ff4d4f",
                        color: "#fff",
                        borderRadius: "10px",
                        minWidth: "20px",
                        height: "20px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "11px",
                        padding: "0 6px",
                        lineHeight: "1",
                        fontWeight: "bold",
                        marginLeft: "8px",
                        flexShrink: 0,
                      }}
                    >
                      {pendingWarehouse.transfers > 99
                        ? "99+"
                        : pendingWarehouse.transfers}
                    </div>
                  )}
                </div>
              </NavLink>
            </Menu.Item>
          )}
          {hasAccessTo("ajustes", "almacen") && (
            <Menu.Item key="ajustes">
              <NavLink to={Global.APP_URLS.STOCK_MODIFICATIONS}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                    overflow: "hidden",
                  }}
                >
                  <span
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Ajustes de Inventario
                  </span>
                  {pendingWarehouse.stockModifications > 0 && (
                    <div
                      style={{
                        backgroundColor: "#ff4d4f",
                        color: "#fff",
                        borderRadius: "10px",
                        minWidth: "20px",
                        height: "20px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "11px",
                        padding: "0 6px",
                        lineHeight: "1",
                        fontWeight: "bold",
                        marginLeft: "8px",
                        flexShrink: 0,
                      }}
                    >
                      {pendingWarehouse.stockModifications > 99
                        ? "99+"
                        : pendingWarehouse.stockModifications}
                    </div>
                  )}
                </div>
              </NavLink>
            </Menu.Item>
          )}
        </Menu.SubMenu>
      )}

      {(hasAccessTo("gestion", "vehiculos") || hasAccessTo("prestamos", "vehiculos")) && (
        <Menu.SubMenu
          icon={!topMenu && <CarOutlined />}
          title="Control Vehicular"
          key="vehiculos"
        >
          {hasAccessTo("gestion", "vehiculos") && (
            <Menu.Item key="vehicles">
              <NavLink to={Global.APP_URLS.VEHICLES}>Vehículos</NavLink>
            </Menu.Item>
          )}
          {hasAccessTo("prestamos", "vehiculos") && (
            <Menu.Item key="vehicle-loans">
              <NavLink to={Global.APP_URLS.VEHICLE_LOANS}>Préstamos</NavLink>
            </Menu.Item>
          )}
        </Menu.SubMenu>
      )}

      {canManage("activos") && (
        <Menu.SubMenu
          icon={!topMenu && <MobileOutlined />}
          title="Control de Activos"
          key="activos"
        >
          {canManage("gestion", "activos") && (
            <Menu.Item key="actives">
              <NavLink to={Global.APP_URLS.ACTIVES}>Activos</NavLink>
            </Menu.Item>
          )}
          {canManage("prestamos", "activos") && (
            <Menu.Item key="active-loans">
              <NavLink to={Global.APP_URLS.ACTIVE_LOANS}>Préstamos de Activos</NavLink>
            </Menu.Item>
          )}
        </Menu.SubMenu>
      )}

      {(hasAccessTo("catalogo", "herramientas") ||
        hasAccessTo("stock", "herramientas") ||
        hasAccessTo("movimientos", "herramientas") ||
        hasAccessTo("ajustes", "herramientas")) && (
        <Menu.SubMenu
          icon={!topMenu && <ToolOutlined />}
          key="herramientas"
          title={
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                paddingRight: "20px",
                overflow: "hidden",
              }}
            >
              <span
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                Herramientas
              </span>
              {canSeeToolAdjustments && pendingToolAdjustments > 0 && (
                <div
                  style={{
                    backgroundColor: "#ff4d4f",
                    color: "#fff",
                    borderRadius: "10px",
                    minWidth: "20px",
                    height: "20px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "11px",
                    padding: "0 6px",
                    lineHeight: "1",
                    fontWeight: "bold",
                    marginLeft: "8px",
                    flexShrink: 0,
                  }}
                >
                  {pendingToolAdjustments > 99
                    ? "99+"
                    : pendingToolAdjustments}
                </div>
              )}
            </div>
          }
        >
          {hasAccessTo("catalogo", "herramientas") && (
            <Menu.Item key="tools-catalog">
              <NavLink to={Global.APP_URLS.TOOLS_CATALOG}>Catálogo</NavLink>
            </Menu.Item>
          )}
          {hasAccessTo("stock", "herramientas") && (
            <Menu.Item key="tools-stock">
              <NavLink to={Global.APP_URLS.TOOLS_STOCK}>Stock por Almacén</NavLink>
            </Menu.Item>
          )}
          {hasAccessTo("movimientos", "herramientas") && (
            <Menu.Item key="tools-movements">
              <NavLink to={Global.APP_URLS.TOOLS_MOVEMENTS}>Movimientos</NavLink>
            </Menu.Item>
          )}
          {hasAccessTo("ajustes", "herramientas") && (
            <Menu.Item key="tools-adjustments">
              <NavLink to={Global.APP_URLS.TOOLS_ADJUSTMENTS}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                    paddingRight: "20px",
                    overflow: "hidden",
                  }}
                >
                  <span
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Ajustes de Inventario
                  </span>
                  {pendingToolAdjustments > 0 && (
                    <div
                      style={{
                        backgroundColor: "#ff4d4f",
                        color: "#fff",
                        borderRadius: "10px",
                        minWidth: "20px",
                        height: "20px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "11px",
                        padding: "0 6px",
                        lineHeight: "1",
                        fontWeight: "bold",
                        marginLeft: "8px",
                        flexShrink: 0,
                      }}
                    >
                      {pendingToolAdjustments > 99
                        ? "99+"
                        : pendingToolAdjustments}
                    </div>
                  )}
                </div>
              </NavLink>
            </Menu.Item>
          )}
        </Menu.SubMenu>
      )}

      {hasAccessTo("reportes") && (
        <Menu.Item icon={!topMenu && <BarChartOutlined />} key="reports">
          <NavLink to={Global.APP_URLS.REPORTS}>Reportes</NavLink>
        </Menu.Item>
      )}

      {hasAccessTo("novedades") && (
        <Menu.Item icon={!topMenu && <HistoryOutlined />} key="novedades">
          <NavLink to={Global.APP_URLS.NOVEDADES}>Novedades</NavLink>
        </Menu.Item>
      )}
    </Menu>
  );
};

const mapStateToProps = (state: any) => ({
  user: state.users?.data || null,
});

export default connect(mapStateToProps)(NavMenu);
